import React, { FC, Fragment, useEffect, useState } from 'react';
import OrdersPageActions from 'components/orders/shared/orders-page-actions.component';
import { NetworkStatus, useQuery } from '@apollo/client';
import { getOrderListRowTemplate } from 'apollo/templates/order/query.graphql';
import { useLiveOrderList } from 'hooks/useLiveOrderList';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { Order, OrderFilter, OrderTableType } from 'models/order';
import { getOrdersForDentistTemplate } from 'apollo/templates/order';
import OrderTableContainer from 'components/orders/order-table-container.container';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import { CONSTANTS } from 'config/constants';
const { LAB_ID_KEY } = CONSTANTS;

type DentistOrdersProps = {
    batchActionSelected: boolean;
    onHandleCheckedOrders: (order: Order, checked: boolean, orderIndex: number) => void;
    onFilterChange?: () => void;
    refetchOrder: boolean;
    isAtLimit?: boolean;
    checkedOrderIds?: Set<string>;
};

const DentistOrders: FC<DentistOrdersProps> = ({
    batchActionSelected,
    onHandleCheckedOrders,
    onFilterChange,
    refetchOrder,
    isAtLimit,
    checkedOrderIds,
}: DentistOrdersProps) => {
    const { t: translate } = useTranslation(['orders']);
    const [orderFilters, setOrderFilters] = useState<OrderFilter>(
        JSON.parse(localStorage.getItem('order-list-filters') || '{}'),
    );
    const { showBoundary } = useDenthubErrorBoundary();

    const liveList = useLiveOrderList('ordersForDentist', getOrderListRowTemplate);
    const { loading, fetchMore, networkStatus, refetch, data } = useQuery(getOrdersForDentistTemplate, {
        context: liveList.context,
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: true,
        variables: { limit: 20, offset: 0, filters: orderFilters, currentLabId: Cookies.get(LAB_ID_KEY) },
        onError(error) {
            showBoundary(error);
        },
    });

    const orders = liveList.ordersFrom(data);
    const lastOffset = orders.length;

    useEffect(() => {
        if (refetchOrder) {
            refetch({
                limit: 20,
                offset: 0,
                filters: orderFilters,
            });
        }
    }, [refetchOrder]);

    const handleFetchMore = () => {
        if (fetchMore) {
            fetchMore({
                variables: {
                    offset: lastOffset,
                },
            });
        }
    };

    const handleFilterChange = (filters: OrderFilter) => {
        setOrderFilters(filters);
        onFilterChange?.();
    };

    const handleUpdateOrderListForArchived = (orderId: string) => liveList.removeOrders([orderId]);

    return (
        <Fragment>
            <Helmet>
                <title>{translate('orders:ORDERS')} | Denthub</title>
            </Helmet>
            <OrdersPageActions
                initFilters={orderFilters}
                onFilterChange={handleFilterChange}
                hideProductionFilter={true}
                hideOrderTagFilter={true}
                hideSupplierFilter={true}
            />
            {loading && networkStatus !== NetworkStatus.fetchMore ? undefined : (
                <OrderTableContainer
                    tableType={OrderTableType.DENTIST}
                    orders={orders}
                    onFetchMore={handleFetchMore}
                    handleUpdateOrderListForArchived={handleUpdateOrderListForArchived}
                    batchActionSelected={batchActionSelected}
                    onHandleCheckedOrders={onHandleCheckedOrders}
                    isAtLimit={isAtLimit}
                    checkedOrderIds={checkedOrderIds}
                />
            )}
        </Fragment>
    );
};

export default DentistOrders;
